import { useEffect, useMemo, useState } from "react";
import type { ComponentType } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  Home,
  LayoutDashboard,
  Library,
  Wrench,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const TABBAR_HEIGHT_PX = 64;

type Tab = {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  comingSoon?: boolean;
};

function useIsAuthenticated() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      setIsAuthenticated(!!session?.user && !session.user.is_anonymous);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setIsAuthenticated(!!session?.user && !session.user.is_anonymous);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return isAuthenticated;
}

export function BottomTabBar({ className }: { className?: string }) {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();

  const shouldHide =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/auth");

  const tabs: Tab[] = useMemo(
    () => [
      { label: "Home", to: "/", icon: Home },
      { label: "Fix", to: "/assistant", icon: Wrench },
      { label: "Guides", to: "/category/all", icon: Library, comingSoon: true },
      { label: "My Home", to: "/home-profile", icon: Building2 },
      {
        label: "Account",
        to: isAuthenticated ? "/profile" : "/auth?redirect=/profile",
        icon: LayoutDashboard,
      },
    ],
    [isAuthenticated],
  );

  if (shouldHide) return null;

  return (
    <nav
      aria-label="Primary"
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        "shadow-[0_-4px_20px_-4px_hsl(var(--foreground)/0.08)]",
        className,
      )}
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div
        className="mx-auto grid w-full max-w-md grid-cols-5"
        style={{ height: TABBAR_HEIGHT_PX }}
      >
        {tabs.map((tab) => {
          if (tab.comingSoon) {
            return (
              <button
                key={tab.label}
                type="button"
                onClick={() => toast("We're improving our repair guides — coming back soon!")}
                className="flex h-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground/50 transition-all active:scale-95"
              >
                <tab.icon className="h-6 w-6" />
                <span className="leading-none font-medium">{tab.label}</span>
              </button>
            );
          }
          return (
            <NavLink
              key={tab.label}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  "flex h-full flex-col items-center justify-center gap-1 text-xs",
                  "transition-all active:scale-95",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )
              }
              end={tab.to === "/"}
            >
              {({ isActive }) => (
                <>
                  <tab.icon className="h-6 w-6" />
                  <span className="leading-none font-medium">{tab.label}</span>
                  {isActive && (
                    <span className="absolute bottom-1 h-1 w-6 rounded-full bg-primary" />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

export const MOBILE_TABBAR_HEIGHT_PX = TABBAR_HEIGHT_PX;
